import { toaster } from "@/components/ui/toaster";
import { AppRoutes } from "@/constants/routes";
import {
  GetProductionGrainsListProps,
  getProductionGrainsList,
} from "@/services/production-grains.service";
import { handlePermissionError } from "@/utils/permission-check";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { Dispatch, SetStateAction } from "react";
import { NavigateFunction } from "react-router-dom";
import { ProductionGrainsListResponse } from "./production-grains.schema";
import { getOptionalBooleanValue } from "@/utils/request-validations";

interface FetchProductionGrainsHandlerProps {
  setLoadingProductionGrains: Dispatch<SetStateAction<boolean>>;
  setProductionGrains: Dispatch<
    SetStateAction<ProductionGrainsListResponse | null>
  >;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  showLoad?: boolean;
  filters?: Partial<GetProductionGrainsListProps>;
}

const getAdmisValues = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.map(Number).filter((item) => !Number.isNaN(item));
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => Number(item.trim()))
      .filter((item) => !Number.isNaN(item));
  }

  return [] as number[];
};

const getProductionGrainsFilter = (
  event: FieldValues,
): Partial<GetProductionGrainsListProps> => {
  const filterObj: Partial<GetProductionGrainsListProps> = {};

  if (event?.Nombre?.trim()) filterObj.Nombre = event.Nombre.trim();

  const aplicaPPMB = getOptionalBooleanValue(event?.AplicaPPMB);
  if (typeof aplicaPPMB === "boolean") filterObj.AplicaPPMB = aplicaPPMB;

  const sumaHA = getOptionalBooleanValue(event?.SumaHA);
  if (typeof sumaHA === "boolean") filterObj.SumaHA = sumaHA;

  const granosAdmis = getAdmisValues(event?.GranosAdmis);
  if (granosAdmis.length) filterObj.GranosAdmis = granosAdmis;

  return filterObj;
};

export const fetchProductionGrainsHandler = async ({
  setLoadingProductionGrains,
  setProductionGrains,
  navigate,
  fetchPermissions,
  showLoad = true,
  filters,
}: FetchProductionGrainsHandlerProps) => {
  try {
    if (showLoad) setLoadingProductionGrains(true);

    const request: GetProductionGrainsListProps = {
      "P.Pagina": (filters as any)?.["P.Pagina"] ?? 1,
      "P.Records": (filters as any)?.["P.Records"] ?? 10,
    };

    if (filters?.Nombre) request.Nombre = filters.Nombre;
    if (typeof filters?.AplicaPPMB === "boolean") {
      request.AplicaPPMB = filters.AplicaPPMB;
    }
    if (typeof filters?.SumaHA === "boolean") request.SumaHA = filters.SumaHA;

    const response = await getProductionGrainsList(request);
    setProductionGrains(response ?? null);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.PRODUCTION_GRAINS,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo cargar el listado de granos productivos.",
      });
    }
  } finally {
    if (showLoad) setLoadingProductionGrains(false);
  }
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<
    SetStateAction<Partial<GetProductionGrainsListProps> | undefined>
  >;
  setPage: Dispatch<SetStateAction<number>>;
  fetchProductionGrains: (
    filters?: Partial<GetProductionGrainsListProps>,
    showLoad?: boolean,
  ) => Promise<void>;
  event: FieldValues;
}

export const submitFiltersHandler = async ({
  event,
  setOpenFilters,
  setFiltersApplied,
  setCurrentFilters,
  setPage,
  fetchProductionGrains,
}: SubmitFiltersHandlerProps) => {
  setFiltersApplied(true);

  const filterObj = getProductionGrainsFilter(event);

  setCurrentFilters(filterObj);
  if (!Object.keys(filterObj).length) setFiltersApplied(false);

  setPage(1);
  await fetchProductionGrains({ ...filterObj, "P.Pagina": 1 }, true);
  setOpenFilters(false);
};

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event?: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
}

export const clearFiltersHandler = async ({
  setValue,
  setFiltersApplied,
  submitFilters,
  getValues,
  setOpenFilters,
}: ClearFiltersHandlerProps) => {
  setValue("Nombre", "");
  setValue("AplicaPPMB", []);
  setValue("SumaHA", []);
  setValue("GranosAdmis", "");

  await submitFilters?.(getValues());
  setFiltersApplied(false);
  setOpenFilters(false);
};
